import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import type {Page} from '@/hooks/empresa/organizacion/use-businesses'
import {track} from '@/lib/telemetry'
import type {ActionNote} from '@/hooks/sgsst/plan/use-actions'
import type {Evidence, UploadTarget} from '@/hooks/sgsst/evidencias/use-evidence'
import type {components} from '@/lib/api-schema'

export type MemberEvidence = components['schemas']['MemberEvidenceRead']
export type MemberAction = components['schemas']['MemberActionRead']
export type SgsstActionStatus = components['schemas']['SgsstActionStatus']
export type PendingEvidence = components['schemas']['PendingEvidenceRead']

export const MEMBER_PAGE_SIZE = 20

// `businessId` undefined disables the query: owners never fire the member surfaces.
export function useMemberSoportes(businessId: string | undefined, page = 1) {
  return useQuery({
    queryKey: ['member', businessId, 'soportes', page],
    queryFn: async () =>
      (
        await api.get<Page<MemberEvidence>>(`/businesses/${businessId}/mi/soportes`, {
          params: {page, page_size: MEMBER_PAGE_SIZE},
        })
      ).data,
    enabled: !!businessId,
    placeholderData: (previous) => previous,
  })
}

// v3.9 A5: open-hallazgo and control-entry photos in one read.
export function useMemberPendingEvidence(businessId: string | undefined) {
  return useQuery({
    queryKey: ['member', businessId, 'evidencia-pendiente'],
    queryFn: async () => (await api.get<PendingEvidence>(`/businesses/${businessId}/mi/evidencia-pendiente`)).data,
    enabled: !!businessId,
  })
}

// `pageSize` deja al home leer solo `total` sin hidratar 20 acciones con evidencias firmadas.
export function useMemberAcciones(
  businessId: string | undefined,
  page = 1,
  estado?: SgsstActionStatus,
  pageSize = MEMBER_PAGE_SIZE
) {
  return useQuery({
    queryKey: ['member', businessId, 'acciones', page, estado ?? null, pageSize],
    queryFn: async () =>
      (
        await api.get<Page<MemberAction>>(`/businesses/${businessId}/mi/acciones`, {
          params: {page, page_size: pageSize, estado},
        })
      ).data,
    enabled: !!businessId,
    placeholderData: (previous) => previous,
  })
}

// Forward-only; the owner-side plan is a different surface, so only member keys are invalidated.
export function useAdvanceAction(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({actionId, status}: {actionId: string; status: SgsstActionStatus}) =>
      api.post(`/businesses/${businessId}/sgsst/actions/${actionId}/estado`, {status}),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({queryKey: ['member', businessId]})
      track('member_action_advanced', {business_id: businessId, status: variables.status})
    },
  })
}

export type MemberPlanActividad = components['schemas']['MemberPlanActividadRead']

export function useMemberPlanTrabajo(businessId: string | undefined, page = 1) {
  return useQuery({
    queryKey: ['member', businessId, 'plan-trabajo', page],
    queryFn: async () =>
      (
        await api.get<Page<MemberPlanActividad>>(`/businesses/${businessId}/mi/plan-trabajo`, {
          params: {page, page_size: MEMBER_PAGE_SIZE},
        })
      ).data,
    enabled: !!businessId,
    placeholderData: (previous) => previous,
  })
}

export function useMarcarMiPlanEjecucion(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({actividadId, mes}: {actividadId: string; mes: number}) =>
      api.post(`/businesses/${businessId}/mi/plan-trabajo/${actividadId}/ejecucion`, {mes}),
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['member', businessId]}),
  })
}

export function useAddMemberActionNote(businessId: string, actionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (text: string) =>
      (await api.post<ActionNote>(`/businesses/${businessId}/mi/acciones/${actionId}/notas`, {text})).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['member', businessId]})
      track('action_note_added', {business_id: businessId})
    },
  })
}

export function useDeleteMemberActionNote(businessId: string, actionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (noteId: string) =>
      api.delete(`/businesses/${businessId}/mi/acciones/${actionId}/notas/${noteId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['member', businessId]})
      track('action_note_removed', {business_id: businessId})
    },
  })
}

export async function mintMemberActionUploadTarget(
  businessId: string,
  actionId: string,
  file: File
): Promise<UploadTarget> {
  return (
    await api.post<UploadTarget>(`/businesses/${businessId}/mi/acciones/${actionId}/upload-target`, {
      filename: file.name,
      content_type: file.type,
      size_bytes: file.size,
    })
  ).data
}

export function rutaDeDescargaDeMiAccion(businessId: string, actionId: string, evidenceId: string): string {
  return `/businesses/${businessId}/mi/acciones/${actionId}/evidencia/${evidenceId}/descarga`
}

export function useAttachMemberActionEvidence(businessId: string, actionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {objectKey: string; file: File}) =>
      (
        await api.post<Evidence[]>(`/businesses/${businessId}/mi/acciones/${actionId}/evidencia`, {
          object_key: input.objectKey,
          filename: input.file.name,
          content_type: input.file.type,
          size_bytes: input.file.size,
        })
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['member', businessId]})
      queryClient.invalidateQueries({queryKey: ['evidence', businessId]})
      track('action_evidence_uploaded', {business_id: businessId})
    },
  })
}
